import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { handleRejectedResponse } from './utils';
import { isEmpty } from 'lodash';
import axios from '../../axios_config';
import axiosSimple from 'axios';
import { notification } from 'antd';
import { toast } from '../hooks/use-toast';
import { STORAGE_KEY } from '../constants/onBoarding';
import { resetStoreReauthFlag } from '../utils/interseptors/pendingRequest';

export const ResetPassword = createAsyncThunk(
  '/auth/reset-password',
  async (data, { getState, rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${process.env.OPULENT_BASE_NODE_API_URL}/auth/reset-password`,
        data
      );
      return response;
    } catch (err) {
      if (err.response) {
        return rejectWithValue({
          err: err.response,
          status: err.response.status
        });
      }
      return rejectWithValue({
        err: 'Network Error'
      });
    }
  }
);

export const TokenVerification = createAsyncThunk(
  '/auth/token-verification',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${process.env.OPULENT_BASE_NODE_API_URL}/auth/token-verification`,
        data
      );
      return response;
    } catch (err) {
      if (err.response) {
        return rejectWithValue({
          err: err.response,
          status: err.response.status
        });
      }
      return rejectWithValue({
        err: 'Network Error'
      });
    }
  }
);

export const SaveNewPassword = createAsyncThunk(
  '/users/save-new-password',
  async (data, { getState, rejectWithValue }) => {
    try {
      const response = await axios.patch(
        '/users/password',
        data
      );
      return response;
    } catch (err) {
      return handleRejectedResponse(err, 'Save New Password', rejectWithValue);
    }
  }
);

export const SignUp = createAsyncThunk(
  '/auth/sign-up',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${process.env.OPULENT_BASE_NODE_API_URL}/auth/sign-up`,
        data
      );
      return {
        ...response,
        data: { ...response?.data, user: data.user }
      };
    } catch (err) {
      return handleRejectedResponse(err, 'Sign Up', rejectWithValue, true, true);
    }
  }
);

export const SendVerificationEmail = createAsyncThunk(
  '/auth/send-verification-email',
  async (data, { getState, rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${process.env.OPULENT_BASE_NODE_API_URL}/auth/send-verification-mail`,
        data
      );
      return response;
    } catch (err) {
      return handleRejectedResponse(err, 'Send Verification Email', rejectWithValue);
    }
  }
);

export const ConfirmTokenVerification = createAsyncThunk(
  '/users/confirm-email-verification',
  async (data, { getState, rejectWithValue }) => {
    const { confirmationToken } = data;
    try {
      const response = await axios.get(
        `/users/confirmation?confirmation_token=${confirmationToken}`,
        data
      );
      return response;
    } catch (err) {
      return handleRejectedResponse(err, 'Sign Up Confirmation', rejectWithValue);
    }
  }
);

export const ResendEmailVerification = createAsyncThunk(
  '/users/resend-email-verification',
  async (data, { getState, rejectWithValue }) => {
    try {
      const response = await axios.post(
        '/users/confirmation',
        data
      );
      return response;
    } catch (err) {
      return handleRejectedResponse(err, 'Resend Verification Email', rejectWithValue);
    }
  }
);

export const SignIn = createAsyncThunk(
  '/auth/sign-in',
  async (data, { getState, rejectWithValue }) => {
    axios.defaults.headers.common.Authorization = ''; // clearing expired token
    try {
      const response = await axios.post(
        `${process.env.OPULENT_BASE_NODE_API_URL}/auth/sign-in`,
        data
      );
      return response;
    } catch (err) {
      return handleRejectedResponse(err, 'Sign In Error', rejectWithValue);
    }
  }
);

export const ImpersonateUser = createAsyncThunk(
  '/impersonate-user',
  async (data, { getState, rejectWithValue }) => {
    const { id } = data;
    try {
      const response = await axios.post(
        `${process.env.OPULENT_NODE_API_URL}/users/${id}/impersonate`
      );
      return response;
    } catch (err) {
      return handleRejectedResponse(err, 'Impersonate User', rejectWithValue);
    }
  }
);

export const SignOut = createAsyncThunk(
  '/users/sign-out',
  async (data, { getState, rejectWithValue }) => {
    try {
      const response = {
        data: {
          status: 200,
          message: 'Logged out successfully'
        }
      };
      localStorage.removeItem(STORAGE_KEY);

      return response;
    } catch (err) {
      return handleRejectedResponse(err, 'Sign Out', rejectWithValue);
    }
  }
);

export const InviteUser = createAsyncThunk(
  '/users/invite-user',
  async (data, { getState, rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${process.env.OPULENT_NODE_API_URL}/users/invite-user`,
        data
      );
      return response;
    } catch (err) {
      return rejectWithValue({ statusCode: err.response?.status, message: err?.response?.data?.message || 'Error in Inviting User' });
    }
  }
);

export const GetWarehouseSellersList = createAsyncThunk(
  '/users/get-warehouse-sellers-list',
  async (data, { getState, rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${process.env.OPULENT_NODE_API_URL}/users/get-warehouse-sellers-list`,
        data
      );
      return response;
    } catch (err) {
      return rejectWithValue({ statusCode: err.response?.status, message: err?.response?.data?.error || 'Error in Getting List of Sellers of Warehouse' });
    }
  }
);

export const ChangeUserInfo = createAsyncThunk(
  '/users/change-user-info',
  async (data, { getState, rejectWithValue }) => {
    try {
      const response = await axios.patch(
        `${process.env.OPULENT_NODE_API_URL}/users/change-user-info`,
        data
      );
      return response;
    } catch (err) {
      return rejectWithValue({ statusCode: err.response?.status, message: err?.response?.data?.error || 'Error in Changing User Info' });
    }
  }
);

// Persist Business Onboarding progress / terminal state (step, state, completed, exited) for the
// logged-in user. The backend echoes the applied column updates in `data`, which we merge into
// currentUser so route gating + the wizard stay in sync without a full re-fetch.
export const UpdateBusinessOnboarding = createAsyncThunk(
  '/users/business-onboarding',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.patch(
        `${process.env.OPULENT_NODE_API_URL}/users/business-onboarding`,
        data
      );
      return response.data;
    } catch (err) {
      return rejectWithValue({ statusCode: err.response?.status, message: err?.response?.data?.message || 'Error updating business onboarding' });
    }
  }
);

export const getUserCurrentSubscription = createAsyncThunk(
  'payment/current',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${process.env.OPULENT_NODE_API_URL}/payment/current`);
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Get User Current Subscription', rejectWithValue);
    }

  }
);

export const getAllSubscriptionPackages = createAsyncThunk(
  'payment/get-subscriptions',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${process.env.OPULENT_NODE_API_URL}/payment/get-subscriptions`);
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Get All Subscription', rejectWithValue);
    }

  }
);

export const getUserBillingHistory = createAsyncThunk(
  'payment/user-billing-history',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${process.env.OPULENT_NODE_API_URL}/payment/user-billing-history`);
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Get User Billing History', rejectWithValue);
    }

  }
);

export const cancelSubscription = createAsyncThunk(
  'payment/cancel-subscription',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${process.env.OPULENT_NODE_API_URL}/payment/cancel-subscription`);
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Cancel Subscription', rejectWithValue);
    }

  }
);

export const updateBillingCardInfo = createAsyncThunk(
  'payment/customer-portal',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${process.env.OPULENT_NODE_API_URL}/payment/customer-portal`);
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Update Billing History', rejectWithValue);
    }

  }
);

export const undoSubscriptionCancel = createAsyncThunk(
  'payment/undo-subscription-cancel',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${process.env.OPULENT_NODE_API_URL}/payment/undo-subscription-cancel`, data);
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Undo Subscription Cancel', rejectWithValue);
    }
  }
);

export const GetCurrentUser = createAsyncThunk(
  '/users/current-user',
  async (data, { getState, rejectWithValue }) => {
    try {
      const response = await axios.get(`${process.env.OPULENT_NODE_API_URL}/users/current-user`);
      return response;
    } catch (err) {
      return handleRejectedResponse(err, 'Sign Up Confirmation', rejectWithValue);
    }
  }
);

export const CreateEmployee = createAsyncThunk(
  '/users/create-employee',
  async (data, { getState, rejectWithValue }) => {
    try {
      const response = await axios.post(`${process.env.OPULENT_NODE_API_URL}/users/create-employee`, data);
      return response;
    } catch (err) {
      return handleRejectedResponse(err, 'Create Employee', rejectWithValue, true, true);
    }
  }
);

export const InviteEmployee = createAsyncThunk(
  '/users/invite-employee',
  async (data, { getState, rejectWithValue }) => {
    try {
      const response = await axios.post(`${process.env.OPULENT_NODE_API_URL}/users/invite-employee`, data);
      return response;
    } catch (err) {
      return handleRejectedResponse(err, 'Invite Employee', rejectWithValue, true, true);
    }
  }
);

export const GetEmployees = createAsyncThunk(
  '/users/get-employees',
  async (data, { getState, rejectWithValue }) => {
    try {
      const response = await axios.get(`${process.env.OPULENT_NODE_API_URL}/users/get-employees`);
      return response;
    } catch (err) {
      return handleRejectedResponse(err, 'Get Employees', rejectWithValue, true, true);
    }
  }
);

export const EditPermissions = createAsyncThunk(
  '/users/edit-permissions',
  async (data, { getState, rejectWithValue }) => {
    try {
      const response = await axios.post(`${process.env.OPULENT_NODE_API_URL}/users/edit-permissions`, data);
      return response;
    } catch (err) {
      return handleRejectedResponse(err, 'Update Permissions', rejectWithValue, true, true);
    }
  }
);

export const SaveColumnPreferences = createAsyncThunk(
  '/users/column-preferences',
  async (data, { getState, rejectWithValue }) => {
    try {
      const response = await axios.post(`${process.env.OPULENT_NODE_API_URL}/users/column-preferences`, data);
      return response;
    } catch (err) {
      return handleRejectedResponse(err, 'Save Column Preferences', rejectWithValue, true, true);
    }
  }
);

export const GetColumnPreferences = createAsyncThunk(
  '/users/column-preferences',
  async (tableName, { getState, rejectWithValue }) => {
    try {
      const response = await axios.get(`${process.env.OPULENT_NODE_API_URL}/users/column-preferences`,
        {
          params: {
            tableName
          }
        }
      );
      return response?.data;
    } catch (err) {
      return handleRejectedResponse(err, 'Get Column Preferences', rejectWithValue, true, true);
    }
  }
);

export const DeleteColumnPreferences = createAsyncThunk(
  '/users/column-preferences',
  async (id, { getState, rejectWithValue }) => {
    try {
      const response = await axios.delete(`${process.env.OPULENT_NODE_API_URL}/users/column-preferences`,
        {
          data: { id }
        }
      );
      return response;
    } catch (err) {
      return handleRejectedResponse(err, 'Delete Column Preferences', rejectWithValue, true, true);
    }
  }
);

const auth = createSlice({
  name: 'auth',
  initialState: {
    user: {
      email: ''
    },
    token: '',
    parentToken: '',
    currentUser: {},
    parentUser: {},
    isImpersonated: false,
    signInLoading: false,
    signUpLoading: false,
    resetPasswordLoading: false,
    tokenVerificationLoading: false,
    newPasswordLoading: false,
    resendEmailLoading: false,
    sendVerificationEmailLoading: false,
    fetchingCurrentUser: false,
    impersonatingUser: false,
    confirmEmailLoading: false,
    isImpersonatedCurrentUser: false,
    signOutLoading: false,
    showFileHeader: false,
    invitingUser: false,
    fetchingWarehouseSellers: false,
    changingUserInfo: false,
    currentActiveSubscription: {},
    fetchingCurrentActiveSubscription: false,
    fetchingAllSubscriptionPackages: false,
    fetchingUserBillingHistory: false,
    updatingBillCardInfo: false,
    updatingUndoSubscriptionCancel: false,
    cancellingSubscription: false,
    creatingEmployee: false,
    invitingEmployee: false,
    fetchingEmployees: false,
    updatingPermissions: false,
    fetchedSubscription: false,
    fetchingColumnPreferences: false,
    deletingColumnPreferences: false,
    savingColumnPreferences: false
  },
  reducers: {
    setField(state, { payload: { module, field, value } }) {
      state[module][field] = value;
    },
    setItem(state, { payload: { field, value } }) {
      state[field] = value;
    },
    stopImpersonatingUser(state) {
      state['token'] = state['parentToken'];
      state['parentToken'] = '';
      state['currentUser'] = state['parentUser'];
      state['parentUser'] = {};
      state['isImpersonated'] = false;
      state['isImpersonatedCurrentUser'] = false;
    },
    clearState(state) {
      state['user']['email'] = '';
      state['token'] = '';
      state['parentToken'] = '';
      state['currentUser'] = {};
      state['parentUser'] = {};
      state['isImpersonated'] = false;
      state['isImpersonatedCurrentUser'] = false;
    },
    setAuthState(_, { payload }) {
      return payload;
    }
  },
  extraReducers: {
    [UpdateBusinessOnboarding.fulfilled]: (state, action) => {
      const updates = action?.payload?.data || {};
      return {
        ...state,
        currentUser: { ...state.currentUser, ...updates }
      };
    },
    [ResetPassword.pending]: (state, action) => ({
      ...state,
      resetPasswordLoading: true
    }),
    [ResetPassword.fulfilled]: (state, action) => {
      const { message } = action?.payload?.data || {};
      if(message.length > 0){
        notification.success({
          message: message,
          top: 65
        });
      }

      return {
        ...state,
        resetPasswordLoading: false
      };
    },
    [ResetPassword.rejected]: (state, action) => {
      const { email } = action?.payload?.err?.data?.errors || {};
      const { message } = action?.payload?.err?.data || {};

      notification.error({
        message: 'Reset Password',
        description: message,
        placement: 'topRight',
        top: 65
      });

      return {
        ...state,
        resetPasswordLoading: false
      };
    },
    [TokenVerification.pending]: (state, action) => ({
      ...state,
      tokenVerificationLoading: true
    }),
    [TokenVerification.fulfilled]: (state, action) => {
      const { current_user } = action?.payload?.data || {};
      return {
        ...state,
        currentUser: {
          ...current_user,
          name: current_user?.full_name
        },
        tokenVerificationLoading: false
      };
    },
    [TokenVerification.rejected]: (state, action) => {
      const { message } = action?.payload?.err?.data || {};

      toast({
        title: 'Token Verification Failed',
        description: message || 'Invalid or expired token',
        variant: 'destructive'
      });

      return {
        ...state,
        tokenVerificationLoading: false
      };
    },
    [SaveNewPassword.pending]: (state, action) => ({
      ...state,
      newPasswordLoading: true
    }),
    [SaveNewPassword.fulfilled]: (state, action) => {
      notification.success({
        message: 'Password updated successfully',
        top: 65
      });
      return {
        ...state,
        newPasswordLoading: false
      };
    },
    [SaveNewPassword.rejected]: (state, action) => {
      return {
        ...state,
        newPasswordLoading: false
      };
    },
    [SignUp.pending]: (state, action) => ({
      ...state,
      signUpLoading: true
    }),
    [SignUp.fulfilled]: (state, action) => {
      const { current_user } = action?.payload?.data || {};
      return {
        ...state,
        currentUser: {
          ...current_user,
          name: current_user?.full_name
        },
        signUpLoading: false
      };
    },
    [SignUp.rejected]: (state, action) => {
      return {
        ...state,
        signUpLoading: false
      };
    },
    [ConfirmTokenVerification.pending]: (state, action) => ({
      ...state,
      confirmEmailLoading: true
    }),
    [ConfirmTokenVerification.fulfilled]: (state, action) => {
      notification.success({
        message: 'Email confirmed',
        top: 65
      });
      return {
        ...state,
        confirmEmailLoading: false
      };
    },
    [ConfirmTokenVerification.rejected]: (state, action) => {
      return {
        ...state,
        confirmEmailLoading: false
      };
    },
    [ResendEmailVerification.pending]: (state, action) => ({
      ...state,
      resendEmailLoading: true
    }),
    [ResendEmailVerification.fulfilled]: (state, action) => {
      notification.success({
        message: 'Email sent successfully',
        top: 65
      });
      return {
        ...state,
        resendEmailLoading: false
      };
    },
    [ResendEmailVerification.rejected]: (state, action) => {
      return {
        ...state,
        resendEmailLoading: false
      };
    },
    [SignIn.pending]: (state, action) => ({
      ...state,
      signInLoading: true
    }),
    [SignIn.fulfilled]: (state, action) => {
      const { current_user } = action?.payload?.data || {};
      notification.success({
        message: 'Signed In successfully',
        top: 65
      });
      return {
        ...state,
        currentUser: {
          ...current_user,
          name: current_user?.full_name
        },
        signInLoading: false
      };
    },
    [SignIn.rejected]: (state, action) => {
      return {
        ...state,
        signInLoading: false
      };
    },
    [GetCurrentUser.pending]: (state, action) => ({
      ...state,
      fetchingCurrentUser: true
    }),
    [GetCurrentUser.fulfilled]: (state, action) => {
      const { current_user } = action?.payload?.data || {};
      return {
        ...state,
        currentUser: {
          ...current_user,
          name: current_user?.full_name
        },
        fetchingCurrentUser: false
      };
    },
    [GetCurrentUser.rejected]: (state, action) => {
      const { statusCode } = action?.payload || {};
      // if (statusCode == 401) {
      //   axios.defaults.headers.common.Authorization = '';
      //   notification.info({
      //     message: "SESSION EXPIRED",
      //     description: 'Un-Authorized, Login Requires',
      //     top: 65
      //   })
      // }

      return {
        ...state,
        // user: {
        //   email: ''
        // },
        // token: '',
        // parentToken: '',
        // currentUser: {},
        // parentUser: {},
        // isImpersonated: false,
        // signInLoading: false,
        // signUpLoading: false,
        // resetPasswordLoading: false,
        // newPasswordLoading: false,
        // resendEmailLoading: false,
        fetchingCurrentUser: false
        // impersonatingUser: false,
        // confirmEmailLoading: false,
        // isImpersonatedCurrentUser: false,
        // signOutLoading: false,
        // showFileHeader: false
      };
    },
    [ImpersonateUser.pending]: (state, action) => ({
      ...state,
      impersonatingUser: true
    }),
    [ImpersonateUser.fulfilled]: (state, action) => {
      const { impersonated_jwt, success, message } = action?.payload?.data || {};
      axios.defaults.headers.common.Authorization = `Bearer ${impersonated_jwt}`;

      return {
        ...state,
        parentToken: isEmpty(state.parentToken) ? state.token : state.parentToken,
        parentUser: isEmpty(state.parentUser) ? state.currentUser : state.parentUser,
        token: impersonated_jwt,
        isImpersonated: true,
        isImpersonatedCurrentUser: false,
        impersonatingUser: false
      };
    },
    [ImpersonateUser.rejected]: (state, action) => {
      return {
        ...state,
        impersonatingUser: false
      };
    },
    [SignOut.pending]: (state, action) => ({
      ...state,
      signOutLoading: true
    }),
    [SignOut.fulfilled]: (state, action) => {
      const { status, message } = action?.payload?.data || {};
      axios.defaults.headers.common.Authorization = '';

      notification.destroy('store-reauth-notification');
      resetStoreReauthFlag();

      notification.success({
        message,
        top: 65
      });

      localStorage.removeItem(STORAGE_KEY);

      return {
        ...state,
        user: {
          email: ''
        },
        token: '',
        parentToken: '',
        currentUser: {},
        parentUser: {},
        isImpersonated: false,
        signInLoading: false,
        signUpLoading: false,
        resetPasswordLoading: false,
        newPasswordLoading: false,
        resendEmailLoading: false,
        fetchingCurrentUser: false,
        impersonatingUser: false,
        confirmEmailLoading: false,
        isImpersonatedCurrentUser: false,
        signOutLoading: false,
        showFileHeader: false,
        currentActiveSubscription: {},
        fetchedSubscription: false
      };
    },
    [SignOut.rejected]: (state, action) => {
      const { message } = action?.payload || {};
      if (message === 'Invalid Token') {
        axios.defaults.headers.common.Authorization = '';

        return {
          ...state,
          user: {
            email: ''
          },
          token: '',
          parentToken: '',
          currentUser: {},
          parentUser: {},
          isImpersonated: false,
          signInLoading: false,
          signUpLoading: false,
          resetPasswordLoading: false,
          newPasswordLoading: false,
          resendEmailLoading: false,
          fetchingCurrentUser: false,
          impersonatingUser: false,
          confirmEmailLoading: false,
          isImpersonatedCurrentUser: false,
          signOutLoading: false,
          showFileHeader: false
        };
      }

      return {
        ...state,
        signOutLoading: false
      };
    },
    [SendVerificationEmail.pending]: (state, action) => ({
      ...state,
      sendVerificationEmailLoading: true
    }),
    [SendVerificationEmail.fulfilled]: (state, action) => {
      return {
        ...state,
        sendVerificationEmailLoading: false
      };
    },
    [SendVerificationEmail.rejected]: (state, action) => {
      return {
        ...state,
        sendVerificationEmailLoading: false
      };
    },
    [InviteUser.pending]: (state, action) => ({
      ...state,
      invitingUser: true
    }),
    [InviteUser.fulfilled]: (state, action) => {
      return {
        ...state,
        invitingUser: false
      };
    },
    [InviteUser.rejected]: (state, action) => {
      return {
        ...state,
        invitingUser: false
      };
    },
    [GetWarehouseSellersList.pending]: (state, action) => ({
      ...state,
      fetchingWarehouseSellers: true
    }),
    [GetWarehouseSellersList.fulfilled]: (state, action) => {
      return {
        ...state,
        fetchingWarehouseSellers: false
      };
    },
    [GetWarehouseSellersList.rejected]: (state, action) => {
      return {
        ...state,
        fetchingWarehouseSellers: false
      };
    },
    [ChangeUserInfo.pending]: (state, action) => ({
      ...state,
      changingUserInfo: true
    }),
    [ChangeUserInfo.fulfilled]: (state, action) => {
      return {
        ...state,
        changingUserInfo: false
      };
    },
    [ChangeUserInfo.rejected]: (state, action) => {
      return {
        ...state,
        changingUserInfo: false
      };
    },
    [getUserCurrentSubscription.pending]: (state) => {
      state.fetchingCurrentActiveSubscription = true;
    },
    [getUserCurrentSubscription.fulfilled]: (state, action) => {
      state.fetchingCurrentActiveSubscription = false;
      state.currentActiveSubscription = action.payload.data;
      state.fetchedSubscription = true;
    },
    [getUserCurrentSubscription.rejected]: (state) => {
      state.fetchingCurrentActiveSubscription = false;
      state.fetchedSubscription = true;
    },
    [getAllSubscriptionPackages.pending]: (state) => {
      state.fetchingAllSubscriptionPackages = true;
    },
    [getAllSubscriptionPackages.fulfilled]: (state) => {
      state.fetchingAllSubscriptionPackages = false;
    },
    [getAllSubscriptionPackages.rejected]: (state) => {
      state.fetchingAllSubscriptionPackages = false;
    }, 
    [undoSubscriptionCancel.pending]: (state) => {
      state.updatingUndoSubscriptionCancel = true;
    },
    [undoSubscriptionCancel.fulfilled]: (state) => {
      state.updatingUndoSubscriptionCancel = false;
    },
    [undoSubscriptionCancel.rejected]: (state) => {
      state.updatingUndoSubscriptionCancel = false;
    },
    [cancelSubscription.pending]: (state) => {
      state.cancellingSubscription = true;
    },
    [cancelSubscription.fulfilled]: (state) => {
      state.cancellingSubscription = false;
    },
    [cancelSubscription.rejected]: (state) => {
      state.cancellingSubscription = false;
    },
    [getUserBillingHistory.pending]: (state) => {
      state.fetchingUserBillingHistory = true;
    },
    [getUserBillingHistory.fulfilled]: (state) => {
      state.fetchingUserBillingHistory = false;
    },
    [getUserBillingHistory.rejected]: (state) => {
      state.fetchingUserBillingHistory = false;
    },
    [updateBillingCardInfo.pending]: (state) => {
      state.updatingBillCardInfo = true;
    },
    [updateBillingCardInfo.fulfilled]: (state) => {
      state.updatingBillCardInfo = false;
    },
    [updateBillingCardInfo.rejected]: (state) => {
      state.updatingBillCardInfo = false;
    },
    [CreateEmployee.pending]: (state) => {
      state.creatingEmployee = true;
    },
    [CreateEmployee.fulfilled]: (state) => {
      state.creatingEmployee = false;
    },
    [CreateEmployee.rejected]: (state) => {
      state.creatingEmployee = false;
    },
    [InviteEmployee.pending]: (state) => {
      state.invitingEmployee = true;
    },
    [InviteEmployee.fulfilled]: (state) => {
      state.invitingEmployee = false;
    },
    [InviteEmployee.rejected]: (state) => {
      state.invitingEmployee = false;
    },
    [GetEmployees.pending]: (state) => {
      state.fetchingEmployees = true;
    },
    [GetEmployees.fulfilled]: (state) => {
      state.fetchingEmployees = false;
    },
    [GetEmployees.rejected]: (state) => {
      state.fetchingEmployees = false;
    },
    [EditPermissions.pending]: (state) => {
      state.updatingPermissions = true;
    },
    [EditPermissions.fulfilled]: (state) => {
      state.updatingPermissions = false;
    },
    [EditPermissions.rejected]: (state) => {
      state.updatingPermissions = false;
    },
    [GetColumnPreferences.pending]: (state) => {
      state.fetchingColumnPreferences = true;
    },
    [GetColumnPreferences.fulfilled]: (state) => {
      state.fetchingColumnPreferences = false;
    },
    [GetColumnPreferences.rejected]: (state) => {
      state.fetchingColumnPreferences = false;
    },
    [DeleteColumnPreferences.pending]: (state) => {
      state.deletingColumnPreferences = true;
    },
    [DeleteColumnPreferences.fulfilled]: (state) => {
      state.deletingColumnPreferences = false;
    },
    [DeleteColumnPreferences.rejected]: (state) => {
      state.deletingColumnPreferences = false;
    },
    [SaveColumnPreferences.pending]: (state) => {
      state.savingColumnPreferences = true;
    },
    [SaveColumnPreferences.fulfilled]: (state) => {
      state.savingColumnPreferences = false;
    },
    [SaveColumnPreferences.rejected]: (state) => {
      state.savingColumnPreferences = false;
    }
  }
});

const { reducer, actions } = auth;
export const { setField, setItem, stopImpersonatingUser, setAuthState } = actions;
export default reducer;
